import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, ShoppingCart, Copy, ChevronRight, Shield,
  RefreshCw, Bot, Megaphone, MessageSquare, CheckCircle,
  AlertTriangle, Code2, Webhook, Package
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsShopifySetup() {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const scopes = [
    { scope: 'read_products', desc: 'Read product titles, descriptions, images, variants, and pricing.' },
    { scope: 'write_products', desc: 'Update product data — used by the Pricing Agent and SEO Agent.' },
    { scope: 'read_orders', desc: 'Read order history for analytics, fulfillment tracking, and CX analysis.' },
    { scope: 'write_orders', desc: 'Update order metadata, tags, and fulfillment status.' },
    { scope: 'read_inventory', desc: 'Read inventory levels across all locations for the Inventory Agent.' },
  ];

  const webhookPayloadProduct = `{
  "topic": "products/update",
  "shop": "your-store.myshopify.com",
  "payload": {
    "id": 7891234567890,
    "title": "Premium Wireless Headphones",
    "vendor": "AudioTech",
    "product_type": "Electronics",
    "variants": [
      {
        "id": 4412345678901,
        "title": "Matte Black",
        "price": "89.99",
        "sku": "ATH-WH-001-BLK",
        "inventory_quantity": 142
      }
    ],
    "updated_at": "2025-12-15T10:30:00-05:00"
  }
}`;

  const webhookPayloadOrder = `{
  "topic": "orders/create",
  "shop": "your-store.myshopify.com",
  "payload": {
    "id": 5567890123456,
    "order_number": 1042,
    "total_price": "179.98",
    "currency": "USD",
    "line_items": [
      {
        "product_id": 7891234567890,
        "variant_id": 4412345678901,
        "title": "Premium Wireless Headphones",
        "quantity": 2,
        "price": "89.99"
      }
    ],
    "created_at": "2025-12-15T11:15:00-05:00"
  }
}`;

  const webhookEvents = [
    { event: 'products/create', desc: 'New product added to Shopify catalog.' },
    { event: 'products/update', desc: 'Product title, price, variant, or inventory changed.' },
    { event: 'products/delete', desc: 'Product removed from catalog.' },
    { event: 'orders/create', desc: 'New order placed by customer.' },
    { event: 'orders/fulfilled', desc: 'Order marked as fulfilled or partially fulfilled.' },
    { event: 'orders/cancelled', desc: 'Order cancelled by merchant or customer.' },
    { event: 'inventory_levels/update', desc: 'Stock level changed at any location.' },
    { event: 'app/uninstalled', desc: 'Merchant uninstalls the Commerce Index app.' },
  ];

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Shopify Integration
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Shopify Setup Guide
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Install Commerce Index from the Shopify App Store and get AI-powered commerce management in minutes.
          Automatic product sync, webhook integration, and one-click agent activation.
        </p>
      </div>

      {/* Step 1: Install */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 1: Install from the Shopify App Store
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <ShoppingCart className="w-8 h-8 text-[#3EF1C7] flex-shrink-0" />
              <div>
                <p className="text-[#E2E8F0] mb-3">
                  Search for <span className="text-[#3EF1C7] font-semibold">"Commerce Index"</span> in the Shopify App Store,
                  or use the direct install link provided during onboarding.
                </p>
                <p className="text-[#94A3B8] mb-3">
                  Click <strong className="text-white">Install</strong> and you'll be redirected to the OAuth authorization flow.
                </p>
                <div className="p-3 bg-[#1E293B] border border-[#334155] rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-[#FFD166]" />
                    <span className="text-[#FFD166] text-sm font-medium">Note</span>
                  </div>
                  <p className="text-sm text-[#94A3B8]">
                    You must be a store owner or have "Manage apps" permission to install Commerce Index.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Step 2: OAuth */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 2: OAuth Authorization
        </h2>
        <p className="text-[#94A3B8] mb-4">
          During installation, Shopify will ask you to approve the following permission scopes.
          Commerce Index only requests what's needed for agent operation:
        </p>
        <div className="space-y-3">
          {scopes.map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-4 bg-[#0D1628] border border-[#22304A] rounded-lg">
              <Shield className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
              <div>
                <code className="text-[#3EF1C7] text-sm font-mono">{item.scope}</code>
                <p className="text-sm text-[#94A3B8] mt-1">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Step 3: Auto Sync */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 3: Automatic Product Sync
        </h2>
        <p className="text-[#94A3B8] mb-4">
          After authorization, Commerce Index performs an initial full sync of your product catalog.
          Going forward, real-time webhook events keep everything in sync automatically.
        </p>

        <h3 className="text-lg font-medium text-white mb-3">Registered Webhook Events</h3>
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          {webhookEvents.map((item, i) => (
            <div key={i} className="flex items-start gap-2 p-3 bg-[#0D1628] border border-[#22304A] rounded-lg">
              <Webhook className="w-4 h-4 text-[#7A5CFF] flex-shrink-0 mt-0.5" />
              <div>
                <code className="text-[#3EF1C7] text-xs font-mono">{item.event}</code>
                <p className="text-xs text-[#94A3B8] mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <h3 className="text-lg font-medium text-white mb-3">Product Update Webhook Payload</h3>
        <div className="relative mb-6">
          <button
            onClick={() => copyToClipboard(webhookPayloadProduct)}
            className="absolute top-3 right-3 p-2 rounded-md bg-[#1E293B] hover:bg-[#334155] transition-colors"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 text-[#64748B]" />
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{webhookPayloadProduct}</code>
          </pre>
        </div>

        <h3 className="text-lg font-medium text-white mb-3">Order Create Webhook Payload</h3>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(webhookPayloadOrder)}
            className="absolute top-3 right-3 p-2 rounded-md bg-[#1E293B] hover:bg-[#334155] transition-colors"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 text-[#64748B]" />
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{webhookPayloadOrder}</code>
          </pre>
        </div>
      </section>

      {/* Step 4: Enable Agents */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 4: Enable AI Agents
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Bot className="w-8 h-8 text-[#3EF1C7] flex-shrink-0" />
              <div>
                <p className="text-[#E2E8F0] mb-3">
                  Navigate to the <span className="text-white font-medium">Agents</span> tab in your Commerce Index dashboard.
                  Toggle on the agents you want to activate. We recommend starting with:
                </p>
                <ul className="space-y-2 mb-3">
                  <li className="flex items-center gap-2 text-[#94A3B8]">
                    <CheckCircle className="w-4 h-4 text-[#3EF1C7]" /> <strong className="text-white">Pricing Agent</strong> — Monitors competitor prices and optimizes your pricing.
                  </li>
                  <li className="flex items-center gap-2 text-[#94A3B8]">
                    <CheckCircle className="w-4 h-4 text-[#3EF1C7]" /> <strong className="text-white">Inventory Agent</strong> — Predicts stockouts and calculates reorder points.
                  </li>
                  <li className="flex items-center gap-2 text-[#94A3B8]">
                    <CheckCircle className="w-4 h-4 text-[#3EF1C7]" /> <strong className="text-white">Deal Agent</strong> — Creates promotions and publishes to thedeals.ai.
                  </li>
                </ul>
                <p className="text-[#94A3B8] text-sm">
                  All agents start in <span className="text-[#FFD166]">observation mode</span> — they'll analyze your data and make recommendations, but won't take action without your approval.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Step 5: Create First Deal */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 5: Create Your First Deal
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Megaphone className="w-8 h-8 text-[#7A5CFF] flex-shrink-0" />
              <div>
                <p className="text-[#E2E8F0] mb-3">
                  Go to the <span className="text-white font-medium">Deals</span> tab and click <strong className="text-[#3EF1C7]">Create Deal</strong>.
                  Select products, set discount parameters, and choose distribution channels.
                </p>
                <p className="text-[#94A3B8] mb-3">
                  Your deal will be published to <span className="text-[#FFD166]">thedeals.ai</span> and can be shared across social media,
                  email campaigns, and messaging channels.
                </p>
                <p className="text-[#94A3B8] text-sm">
                  Once the Deal Agent is active, it will automatically suggest promotions based on inventory levels,
                  competitor pricing, and seasonal trends.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Step 6: Connect Messaging */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 6: Connect Messaging
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <MessageSquare className="w-8 h-8 text-[#FFD166] flex-shrink-0" />
              <div>
                <p className="text-[#E2E8F0] mb-3">
                  Connect OpenClaw to manage your store from Telegram, WhatsApp, Slack, or iMessage.
                  Link your account with a one-time verification code and start issuing commands.
                </p>
                <p className="text-[#94A3B8] mb-3">
                  Try <code className="text-[#3EF1C7] bg-[#0B1120] px-2 py-0.5 rounded">/pulse</code> to see your store health,
                  or <code className="text-[#3EF1C7] bg-[#0B1120] px-2 py-0.5 rounded">/deals</code> to view active promotions.
                </p>
                <Link to="/docs/openclaw" className="text-[#3EF1C7] hover:underline text-sm flex items-center gap-1">
                  Full OpenClaw setup guide <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Embedded App Option */}
      <section className="mb-12">
        <div className="p-4 bg-[#1E293B] border border-[#334155] rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Code2 className="w-5 h-5 text-[#38BDF8]" />
            <span className="text-white font-medium">Shopify Embedded App</span>
          </div>
          <p className="text-sm text-[#94A3B8] mb-2">
            Commerce Index can also run as an embedded app directly inside your Shopify admin panel.
            This gives you access to the full dashboard without leaving Shopify.
          </p>
          <Link to="/docs/shopify-embedded" className="text-[#3EF1C7] hover:underline text-sm flex items-center gap-1">
            Learn about the embedded app option <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </section>

      {/* What's Next */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          What's Next?
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <ul className="space-y-3">
              {[
                { text: 'Explore all 80+ AI agents', link: '/docs/agents' },
                { text: 'Set up OpenClaw messaging', link: '/docs/openclaw' },
                { text: 'Follow the Onboarding Checklist', link: '/docs/onboarding-checklist' },
                { text: 'Shopify Embedded App setup', link: '/docs/shopify-embedded' },
                { text: 'REST API Reference', link: '/docs/rest-api' },
              ].map((item, i) => (
                <li key={i}>
                  <Link to={item.link} className="flex items-center gap-2 text-[#3EF1C7] hover:underline">
                    <ChevronRight className="w-4 h-4" />
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
